import { useEffect, useState, useMemo } from "react";
import useCurrentUser from "./useCurrentUser";

// Breakpoints can be adjusted as needed
const MOBILE_MAX = 767;
const TABLET_MAX = 1024;

const getScreenType = (width) => {
  if (width <= MOBILE_MAX) return "mobile";
  if (width <= TABLET_MAX) return "tablet";
  return "laptop";
};

/**
 * useAttendanceScreenRules
 *
 * - Derives screen type: "mobile" | "tablet" | "laptop"
 * - Combines it with the current user's workMode to drive attendance rules.
 *
 * Rules (as requested):
 * - If user is logged in and workMode is "WFH" → do nothing (no special restriction).
 * - If workMode is "WFO" and screen is mobile → block / show message for
 *   clock in/out and break in/out.
 */
const useAttendanceScreenRules = () => {
  const { user } = useCurrentUser();
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const screenType = useMemo(() => getScreenType(width), [width]);
  const workMode = user?.workMode || null;

  const isMobile = screenType === "mobile";

  // true => user is WFO and using a mobile-sized screen
  const shouldBlockAttendanceOnMobile =
    Boolean(user) && isMobile && workMode === "WFO";

  return {
    screenType,                    // "mobile" | "tablet" | "laptop"
    width,                         // current window width
    isMobile,
    workMode,                      // from current user
    shouldBlockAttendanceOnMobile, // true => show "not allowed" message
  };
};

export default useAttendanceScreenRules;

